import PropTypes from 'prop-types';
import { getRequestTypeForActions } from '../triggers/requests';
import { callTriggerAction } from '../../actions/custom-triggers';
import { buildActionPayload } from '../triggers/payload';
import interactiveConfig from '../triggers/triggers-config';
import { checkConditionAsync } from '../triggers/conditions';
import { asyncForEach } from '../utils/async-for-each';
import OrganizationService from '../../services/org-service';
import {
  getDeepLinkIfPresent,
  removeSignupRedirect,
} from '../../services/signup-service';
import { requestHandoffToken } from '../../services/handoff-service';
import { logError, logInfo } from '../../analytics/logger/logger';

class TriggerComponent {
  constructor() {
    this.totalCallsToMake = 0;
    this.numberOfMadeCalls = 0;
    this.triggers = [];
    this.redirectURLs = [];
    this.hasFailed = false;
    this.handoffToken = null;
  }

  // Mint the mobile handoff token BEFORE the step's triggers fire. One of the
  // last-step triggers logs the user out (clearing cookies), and minting requires an
  // authenticated session — so minting after the triggers run would 403. We capture the
  // token here while still authenticated and append it to the deep link at redirect time.
  // The minted token is an independent credential that survives the web-session logout.
  // No-op for the existing web flow (no sleepio:// deep link present), so it cannot affect it.
  async prefetchHandoffToken() {
    this.handoffToken = null;
    if (this.isLastStep && getDeepLinkIfPresent()) {
      logInfo('handoff_token_prefetch_started', { hasDeepLink: true });
      this.handoffToken = await requestHandoffToken();
      if (this.handoffToken) {
        logInfo('handoff_token_prefetch_success', {
          tokenLength: this.handoffToken.length,
        });
      } else {
        logError('handoff_token_prefetch_empty', { hasDeepLink: true });
      }
    }
  }

  async fireTriggers(triggers, isLastStep = false) {
    this.isLastStep = isLastStep;
    this.triggers = triggers;
    await this.prefetchHandoffToken();
    this.triggers.forEach(trigger => {
      this.totalCallsToMake += trigger.action_ids.length;
    });
    this.triggers.forEach(trigger => {
      asyncForEach(trigger.action_ids, async action => {
        await this.callAction(
          action,
          trigger.product_id,
          trigger.organization_id,
          trigger.action_ids.length
        );
      });
    });
  }

  async triggersFinishedFiring() {
    // On the final step, the deep link takes priority over any backend redirect URL
    // so the native app receives the callback after the questionnaire completes.
    // On intermediate steps (e.g. account creation), isLastStep is false so the
    // backend redirect URL (to the questionnaire) is used instead.
    if (this.isLastStep) {
      const deepLink = getDeepLinkIfPresent();
      if (deepLink) {
        // Use the token pre-minted in prefetchHandoffToken() — BEFORE the logout trigger
        // fired — so the mint happened while still authenticated. Minting here (after the
        // triggers ran) would hit a logged-out session and 403.
        const token = this.hasFailed ? null : this.handoffToken;
        const tokenParam = token ? `&token=${token}` : '';
        const status = this.hasFailed || !token ? 'error' : 'success';
        logInfo('handoff_deep_link_redirect', {
          status,
          hasFailed: this.hasFailed,
          hasToken: !!token,
          tokenLength: token ? token.length : 0,
        });
        removeSignupRedirect();
        const redirectTarget = `${deepLink}?status=${status}${tokenParam}`;
        window.location.href = redirectTarget;
        return;
      }
    }
    if (this.redirectURLs.length) {
      const fallbackUrl = this.redirectURLs[this.redirectURLs.length - 1];
      window.location = fallbackUrl;
    }
  }

  callAction(action, product_id, organization_id) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async resolve => {
      action = JSON.parse(JSON.stringify(action));
      const parameterRenaming = interactiveConfig.REQUESTS.PARAMETER_RENAMING;
      const parameterValueChange =
        interactiveConfig.REQUESTS.PARAMETER_VALUE_CHANGE;

      if (action) {
        let entity;
        let method;

        // conditions is an array in which we define objects describing a condition
        // objects are treated as an OR condition -> conditions in object 1 must be met OR conditions in object 2
        // keys inside the object are AND condition -> all the conditions defined as keys must be met
        // i.e. {post_test: false}, {user.idp_name: email} -> either not a post test, OR the user signed up with email
        // i.e. {post_test: false, user.idp_name: email} -> not a post test AND user signed up with email

        // ... therefore, if the action has the wait_for_pending_apis flag, with add this flag to each condition object
        action.conditions = action.conditions || [];
        action.conditions = action.conditions.map(condition => {
          if (action.wait_for_pending_apis) {
            condition.has_pending_requests = false;
          }

          return condition;
        });

        // ... if action had no conditions but the flag was still set, make sure to set it now as a condition
        if (!action.conditions.length) {
          action.conditions.push({ has_pending_requests: false });
        }

        action.hidden_inputs = action.params;
        const organizationId = OrganizationService.getOrgId();

        const request = getRequestTypeForActions(action);
        const payload = buildActionPayload(
          [],
          action,
          parameterRenaming,
          parameterValueChange
        );
        const validForCurrentProduct = product_id
          ? product_id === window.product_id
          : true;
        const validForCurrentOrg = organization_id
          ? organization_id === organizationId
          : true;

        entity = request.service;
        method = request.method;

        // eslint-disable-next-line max-len
        if (
          validForCurrentProduct &&
          validForCurrentOrg &&
          (await checkConditionAsync(null, '', action.conditions, true))
        ) {
          callTriggerAction(
            entity,
            method,
            payload,
            action.service_version,
            (redirectURLs, hasError) => {
              if (hasError) {
                this.hasFailed = true;
              }
              if (redirectURLs) {
                this.redirectURLs.push(redirectURLs);
              }

              this.numberOfMadeCalls += 1;
              if (this.totalCallsToMake === this.numberOfMadeCalls) {
                this.triggersFinishedFiring();
              }

              resolve();
            }
          );
        }
      }
    });
  }
}

TriggerComponent.propTypes = {
  triggerActions: PropTypes.object,
};

export default TriggerComponent;
